import * as apis from './api'
import { LANGUAGE } from '@/constants'
import { snakeCase } from 'lodash'
import { useEffect } from 'react'

// retry define
const RETRY_TIMES = 3
let current_retry_time = 0

// object convert to snakeCase
const object2snakeCase = (o: Record<string, any>) => {
  return Object.keys(o).reduce((acc, key) => {
    acc[snakeCase(key)] = o[key]
    return acc
  }, {} as Record<string, any>)
}

export const getIntercomConfig = (interComConfig = {}) => {
  const selfLanguageInfo = LANGUAGE.filter((item) => item.lang === window.localStorage.i18nextLng)
  const localLang = selfLanguageInfo[0] ? selfLanguageInfo[0].languageType : 'en'
  return Object.assign(
    { default_language_code: localLang, alignment: 'right', horizontal_padding: 20, vertical_padding: 20 },
    object2snakeCase(interComConfig)
  ) as IIntercomSettings
}

const promiseQueue = []
/**
 * 多次调用 getIntercom 在保证sdk已经加载完成的时候同步promise状态
 */
function lock(): Promise<any> {
  if (window.Intercom) return Promise.resolve()
  return new Promise((resolve, reject) => {
    promiseQueue.push({ resolve, reject })
  })
}

/**
 * 插入Intercom SDK
 */
function insertScript(appId: string) {
  const script = document.createElement('script')
  script.async = true
  script.src = `https://${appId}.intercom-chat.com/shim.latest.js`
  script.onload = () => {
    promiseQueue.forEach(({ resolve }) => {
      resolve()
    })
  }
  /**
   * error retry
   */
  script.onerror = () => {
    if (current_retry_time >= RETRY_TIMES) {
      promiseQueue.forEach(({ reject }) => {
        reject()
      })
    } else {
      current_retry_time++
      insertScript(appId)
    }
  }
  const x = document.getElementsByTagName('script')[0]
  x.parentNode?.insertBefore(script, x)
}

export async function getIntercom(): Promise<any> {
  await lock()
  const w = window
  const ic = w.Intercom

  // if (typeof ic === 'function') {
  ic('reattach_activator')
  ic('update', w.intercomSettings)

  return ic
}

export const insertIntercom = async () => {
  const { data: intercomConfig } = await apis.getIntercomConfig()

  // 没有 appid 就不初始化，防止切换语言或者其他误调用
  if (!intercomConfig.appId) throw new Error('appid not found')

  insertScript(intercomConfig.appId)

  window.intercomSettings = getIntercomConfig(intercomConfig)
  getIntercom()
}

export function updateIntercom(data?: Record<string, any>) {
  const w = window

  return getIntercom().then((ic) => {
    w.intercomSettings = {
      ...getIntercomConfig(),
      ...(data ?? {}),
    }
    ic('update', { ...w.intercomSettings })
  })
}

export function updateIntercomLanguage() {
  updateIntercom()
}

export function shutdownIntercom() {
  const w = window
  const ic = w.Intercom
  ic?.('shutdown')
  w.intercomSettings = undefined
}

export const intercomHandle = {
  hidden() {
    getIntercom().then((ic) => {
      ic('shutdown')
    })
  },
  show() {
    getIntercom().then((ic) => {
      const w = window
      ic('update', { ...w.intercomSettings })
    })
  },
  /**
   * 打开客服弹窗
   */
  open() {
    getIntercom().then((ic) => {
      ic('show')
    })
  },
}

export const useHiddenIntercom = () => {
  useEffect(() => {
    intercomHandle.hidden()
    return () => {
      intercomHandle.show()
    }
  }, [])
}
